import React, { useEffect, useMemo } from "react";

import { faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Formik, getIn } from "formik";
import { isEqual } from "lodash";
import uniqBy from "lodash/uniqBy";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { closeConfirmModal, openConfirmModal, SaleAction } from "actions";
import { SaleAttributesAction } from "actions/saleAttributes";

import { Row } from "components/Layout";

import { SaleyardPermissions } from "constants/permissions";
import { PricingTypes } from "constants/pricingTypes";
import {
  SaleyardSaleTypes,
  UnselectableSaleTypes,
  SaleRoundName,
  SaleType,
} from "constants/sale";
import { Species } from "constants/species";

import { toSelectOption } from "lib";

import {
  convertDateTimeToUTCDateTime,
  formatISO8601DateString,
  formatUTCToLocalDateTimeInputString,
} from "lib/timeFormats";
import { commissionsAreEqual } from "lib/vendorCommission";

import {
  getActiveLivestockAgentDeployment,
  getActiveRole,
  getDefaultRounds,
  getDeployments,
  getIsConsigningAgent,
  getIsLivestockAgent,
  getRounds,
  getSaleById,
  getSaleSubTypes,
  getSaleTypes,
  selectCurrentSpecies,
} from "selectors";
import {
  selectCanEditSaleRoles,
  selectCanViewSaleRoles,
} from "selectors/saleRoles";

import { useHasSaleyardPermission } from "hooks";

import BobbyCalfSaleForm from "./BobbyCalfSaleForm";
import SaleForm from "./SaleForm";
import { validationSchema } from "./SaleValidationSchema";

const DisabledLabel = ({ saleType }) => (
  <span style={{ color: "gray" }}>
    <FontAwesomeIcon icon={faLock} />
    {"   "}
    {saleType}
  </span>
);

function SaleModal({
  closeSelf,
  isScaleOperator,
  saleyards,
  buyers,
  initialValues,
  saleId,
  validateSale,
}) {
  const defaultRounds = useSelector(getDefaultRounds);
  const saleTypes = useSelector(getSaleTypes);
  const rounds = useSelector(getRounds);
  const species = useSelector(selectCurrentSpecies);
  const selectedSale = useSelector(getSaleById(saleId));
  const deployments = useSelector(getDeployments);
  const currentDeployment = useSelector(getActiveLivestockAgentDeployment);
  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const isConsigningAgent = useSelector(getIsConsigningAgent);
  const currentUserId = useSelector(getActiveRole)?.user_id ?? null;
  const canViewSaleRoles = useSelector(selectCanViewSaleRoles);
  const canEditSaleRoles = useSelector(selectCanEditSaleRoles);

  // View-only: can create only Paddock / Over Hooks and is auto-assigned.
  const isSaleRolesViewOnly = canViewSaleRoles && !canEditSaleRoles;

  const dispatch = useDispatch();

  const filterDefaultRounds = React.useCallback(
    props => {
      const { sale_type, saleyard_id, species_id, sale_sub_type_id } = props;
      const speciesRounds = Object.values(rounds).filter(
        r => r.species_id === species_id,
      );

      // Handle rounds for sale types that don't _really_ have rounds (BobbyCalf and ExternalAgentSale)
      const hardCoded = Object.values(speciesRounds).reduce((acc, round) => {
        if (
          sale_type === SaleType.BOBBYCALF &&
          round.name === SaleRoundName.BobbyCalf
        ) {
          acc.push(toSelectOption(round));
        }

        if (
          sale_type === SaleType.EXTERNAL_AGENT_SALE &&
          round.name === SaleRoundName.ExternalAgentSale
        ) {
          acc.push(toSelectOption(round));
        }

        if (
          sale_type === SaleType.SELL_VIA_EXTERNAL_AGENT &&
          round.name === SaleRoundName.SellViaExternalAgent
        ) {
          acc.push(toSelectOption(round));
        }

        return acc;
      }, []);
      if (hardCoded.length > 0) {
        return hardCoded;
      }
      const saleSubTypeFilteredDefaultRoundConfig = defaultRounds.filter(
        round => {
          if (round.sale_type !== sale_type) {
            return false;
          } else {
            return (
              round.sale_sub_type_id === sale_sub_type_id ||
              (!round.sale_sub_type_id && !sale_sub_type_id)
            );
          }
        },
      );

      // If any of the defaults are narrowed down explicity for this yard, we should use just that set.
      const yardFilteredDefaultRoundConfig =
        saleSubTypeFilteredDefaultRoundConfig.filter(
          round => round.saleyard_id === saleyard_id,
        );
      const generalYardDefaultRoundConfig =
        saleSubTypeFilteredDefaultRoundConfig.filter(
          round => !round.saleyard_id,
        );

      const defaultRoundConfig =
        yardFilteredDefaultRoundConfig.length > 0
          ? yardFilteredDefaultRoundConfig?.[0]
          : generalYardDefaultRoundConfig?.[0];

      // No default configured for this sale - nothing to do!
      if (!defaultRoundConfig) {
        return [];
      }

      const filteredRounds = defaultRoundConfig.rounds
        .filter(round => round.species_id === species_id)
        .map(round => toSelectOption(round));

      // Only return the unique default rounds from the saleyard or global defaults.
      return uniqBy(filteredRounds, "value");
    },
    [defaultRounds, rounds],
  );

  const subTypes = useSelector(getSaleSubTypes);
  const filterSaleSubTypes = React.useCallback(
    props => {
      const { sale_type, species_id } = props;
      return Object.values(subTypes).filter(
        type => type.saleType === sale_type && type.speciesId === species_id,
      );
    },
    [subTypes],
  );

  const isEditMode = !!saleId;

  const sale = React.useMemo(() => {
    return selectedSale
      ? {
          ...selectedSale,
          rounds: selectedSale?.rounds.map(roundId =>
            toSelectOption(rounds[roundId]),
          ),
        }
      : null;
  }, [rounds, selectedSale]);

  const defaultSpecies = isScaleOperator
    ? species.find(s => s.id === Species.CATTLE)
    : species[0] || {};

  const hasCanBlockNonAdminChangingWeightsPermission = useHasSaleyardPermission(
    SaleyardPermissions.canBlockNonAdminChangingWeights,
  );

  const defaultSaleyardId = useMemo(() => {
    if (sale?.saleyard_id) {
      return sale.saleyard_id;
    } else if (initialValues.saleyard_id) {
      return initialValues.saleyard_id;
    } else if (saleyards.length > 0) {
      return saleyards[0].value;
    } else {
      return undefined;
    }
  }, [initialValues.saleyard_id, sale?.saleyard_id, saleyards]);

  const deploymentsFilteredByLocation = useMemo(
    () =>
      Object.values(deployments).filter(deployment =>
        deployment.locations.some(
          location => location.saleyard_id === defaultSaleyardId,
        ),
      ),
    [defaultSaleyardId, deployments],
  );

  const defaultDeploymentIds = useMemo(
    () => deploymentsFilteredByLocation.map(value => value.id),
    [deploymentsFilteredByLocation],
  );

  const defaults = {
    block_non_admin_changing_weights:
      !!hasCanBlockNonAdminChangingWeightsPermission,
    default_property_id: null,
    default_buyer_id: null,
    default_vendor_property_id: null,
    default_vendor_id: null,
    sale_type: saleTypes.includes(SaleType.SALEYARD_AUCTION)
      ? saleTypes[saleTypes.indexOf(SaleType.SALEYARD_AUCTION)]
      : saleTypes[0],
    date: formatISO8601DateString(new Date()),
    manual_vendor_numbers: false,
    species_id: defaultSpecies.id,
    pricing_type_id: defaultSpecies.defaultPricingType,
    default_buyers_premium: undefined,
    default_listing_fee_cents: undefined,
    card_transaction_fee_percentage: undefined,
    deployment_count: sale?.deployment_count,
    is_sale_locked:
      sale?.deployment_sales.some(obj => obj.is_sale_locked === true) || false,
    deploymentIds: defaultDeploymentIds,
    // if we are a livestock agent only provide deployment sale
    // data for the agents current deployment.
    deployment_sales: isLivestockAgent
      ? [{ deployment_id: currentDeployment.id }]
      : defaultDeploymentIds.map(id => ({
          deployment_id: id,
          vendorCommission: null,
        })),
    saleyard_id: defaultSaleyardId,
    manager_ids: [],
    assignee_ids: [],
  };

  // When a sale_type is pre-filled via initialValues (e.g. from the mobile sale type picker),
  // compute rounds and sub-types using the effective merged values so they start correctly.
  const effectiveDefaults = isEditMode
    ? defaults
    : { ...defaults, ...initialValues };
  const defaultFilteredSubTypes = filterSaleSubTypes(effectiveDefaults);
  const [initial, setInitial] = React.useState({
    // Apply the defaults
    ...defaults,
    rounds: filterDefaultRounds(effectiveDefaults),
    sale_sub_type_id:
      defaultFilteredSubTypes.length === 1
        ? defaultFilteredSubTypes[0]?.id
        : null,
    // Apply any initial values (eg currently selected saleyard)
    ...initialValues,
    // If its edit mode, apply the selected sale.
    ...sale,
    // In edit mode, derive deploymentIds from the sale's deployment_sales
    ...(sale
      ? {
          deploymentIds: sale.deployment_sales.map(ds => ds.deployment_id),
        }
      : {}),
    // View-only create: pre-fill the creator as assignee.
    ...(!isEditMode && isSaleRolesViewOnly && currentUserId
      ? { assignee_ids: [currentUserId] }
      : {}),
    // In edit mode, seed the role fields from the sale's existing roles.
    ...(isEditMode && sale
      ? {
          manager_ids: sale.sale_attributes?.managerIds || [],
          assignee_ids: sale.sale_attributes?.assigneeIds || [],
        }
      : {}),
  });

  const speciesRounds = useMemo(
    () =>
      Object.values(rounds).filter(r => r.species_id === initial.species_id),
    [rounds, initial.species_id],
  );

  const handleReInit = React.useCallback(
    values => {
      const nextValues = { ...values };

      nextValues.start_datetime = sale?.start_datetime
        ? formatUTCToLocalDateTimeInputString(sale.start_datetime)
        : "";
      nextValues.end_datetime = sale?.end_datetime
        ? formatUTCToLocalDateTimeInputString(sale.end_datetime)
        : "";
      nextValues.is_sale_locked =
        sale?.deployment_sales.some(obj => obj.is_sale_locked === true) ||
        false;

      // We don't show the species for clearing sales  or sundry sales.
      // So when changing to one of these sale types, we need to update the species.
      if (nextValues.sale_type === SaleType.CLEARING) {
        nextValues.species_id = Species.CLEARING_SALE;
      } else if (nextValues.sale_type === SaleType.SUNDRY_SALE) {
        nextValues.species_id = Species.SUNDRY_SALE;
      }

      if (
        [
          SaleType.CLEARING,
          SaleType.EXTERNAL_AGENT_SALE,
          SaleType.SELL_VIA_EXTERNAL_AGENT,
          SaleType.ON_FARM_AUCTION,
          SaleType.OVER_HOOKS,
          SaleType.PADDOCK,
          SaleType.SUNDRY_SALE,
          SaleType.REFERENCE_SALE,
        ].includes(nextValues.sale_type)
      ) {
        // For these sale types, the saleyard is resolved on the backend
        nextValues.saleyard_id = undefined;
      } else if (SaleyardSaleTypes.includes(nextValues.sale_type)) {
        // Saleyard sale type - (re) set the default saleyard from initial value (if provided), otherwise use the first saleyard or undefined
        nextValues.saleyard_id = defaultSaleyardId || undefined;
      }

      const saleSubTypesForSale = filterSaleSubTypes(nextValues);

      const isSingleDeploymentSaleType = !SaleyardSaleTypes.includes(
        nextValues.sale_type,
      );

      const deploymentSales = isSingleDeploymentSaleType
        ? nextValues.deployment_sales.filter(
            ds => ds.deployment_id === currentDeployment.id,
          )
        : nextValues.deployment_sales;

      const deploymentIds = deploymentSales.map(ds => ds.deployment_id);

      if (saleSubTypesForSale.length === 1) {
        nextValues.sale_sub_type_id = saleSubTypesForSale[0]?.id;
      } else if (
        !saleSubTypesForSale
          .map(obj => obj.id)
          .includes(nextValues.sale_sub_type_id)
      ) {
        nextValues.sale_sub_type_id = null;
      }

      // View-only users: creator must stay an assignee on the sale they create
      // (re-add if they removed themselves).
      if (!isEditMode && isSaleRolesViewOnly && currentUserId) {
        if (!(nextValues.assignee_ids || []).includes(currentUserId)) {
          nextValues.assignee_ids = [
            ...(nextValues.assignee_ids || []),
            currentUserId,
          ];
        }
      }

      // Seed role fields from the sale on mount, only when absent so
      // in-progress edits aren't clobbered.
      if (isEditMode) {
        if (nextValues.manager_ids === undefined) {
          nextValues.manager_ids = sale?.sale_attributes?.managerIds || [];
        }
        if (nextValues.assignee_ids === undefined) {
          nextValues.assignee_ids = sale?.sale_attributes?.assigneeIds || [];
        }
      }

      // When certain fields are edited, other fields may need to be updated.
      // eg species -> pricing_type & rounds
      if (nextValues.sale_type === SaleType.OVER_HOOKS) {
        setInitial({
          ...nextValues,
          pricing_type_id: PricingTypes.PER_KILO,
          rounds: filterDefaultRounds(nextValues),
          deploymentIds,
          deployment_sales: deploymentSales,
          ...(sale || {}),
        });
      } else if (nextValues.sale_type === SaleType.CLEARING) {
        setInitial({
          ...nextValues,
          default_buyers_premium: getIn(
            nextValues,
            "deployment_sales[0].default_buyers_premium",
          ),
          default_listing_fee_cents: getIn(
            nextValues,
            "deployment_sales[0].default_listing_fee_cents",
          ),
          card_transaction_fee_percentage: getIn(
            nextValues,
            "deployment_sales[0].card_transaction_fee_percentage",
          ),
          export_sites: getIn(nextValues, "deployment_sales[0].export_sites"),
          rounds: filterDefaultRounds(nextValues),
          deploymentIds,
          deployment_sales: deploymentSales,
          ...(sale || {}),
        });
      } else {
        setInitial({
          ...nextValues,
          pricing_type_id: species.find(s => s.id === nextValues.species_id)
            ?.defaultPricingType,
          rounds: filterDefaultRounds(nextValues),
          deploymentIds,
          deployment_sales: deploymentSales,
          ...(sale || {}),
        });
      }
    },
    [
      sale,
      filterSaleSubTypes,
      currentDeployment,
      filterDefaultRounds,
      species,
      defaultSaleyardId,
      currentUserId,
      isEditMode,
      isSaleRolesViewOnly,
    ],
  );

  const onDelete = () => {
    dispatch(
      openConfirmModal({
        title: "Are you sure?",
        message: "Are you sure you want to delete this sale?",
        actions: [
          {
            label: "No",
            secondary: true,
            onClick: () => dispatch(closeConfirmModal()),
          },
          {
            label: "Yes",
            onClick: () => {
              dispatch(SaleAction.checkAndDeleteCurrent());
              dispatch(closeConfirmModal());
              closeSelf();
            },
          },
        ],
      }),
    );
  };

  useEffect(() => {
    // If the underlying sale changes from somewhere else, update the form
    if (sale) {
      handleReInit(sale);
    }
  }, [sale, handleReInit]);

  // This is fired on bobbycalf when updating the default business, it only gets set, not cleared.
  useEffect(() => {
    const defaultBuyerChanged =
      initialValues.default_buyer_id &&
      initialValues.default_buyer_id !== initial.default_buyer_id;

    const defaultPropertyChanged =
      initialValues.default_property_id &&
      initialValues.default_property_id !== initial.default_property_id;

    if (defaultBuyerChanged || defaultPropertyChanged) {
      handleReInit({
        ...initial,
        default_buyer_id: initialValues.default_buyer_id,
        default_property_id: initialValues.default_property_id,
      });
    }
  }, [handleReInit, initialValues, initial]);

  // Persist role edits via the SaleAttributes endpoint (reducer applies the
  // optimistic update). Create persists roles in the sale-create payload.
  const persistSaleRoles = values => {
    if (!canEditSaleRoles) {
      return;
    }
    const existing = selectedSale?.sale_attributes || null;
    const currentManagerIds = existing?.managerIds || [];
    const currentAssigneeIds = existing?.assigneeIds || [];
    const nextManagerIds = values.manager_ids || [];
    const nextAssigneeIds = values.assignee_ids || [];

    const unchanged =
      isEqual([...currentManagerIds].sort(), [...nextManagerIds].sort()) &&
      isEqual([...currentAssigneeIds].sort(), [...nextAssigneeIds].sort());
    if (unchanged) {
      return;
    }

    const livestockSaleId = values.livestocksale_id;
    // Status and roles share one SaleAttributes row, so reuse its id.
    const attributesId = existing?.id || null;
    const base = {
      livestockSaleId,
      // Preserve the current status so a role edit doesn't clear it.
      saleStatusId: existing?.saleStatusId ?? null,
      managerIds: nextManagerIds,
      assigneeIds: nextAssigneeIds,
    };

    dispatch(
      attributesId
        ? SaleAttributesAction.update({ id: attributesId, ...base })
        : SaleAttributesAction.create({ id: uuidv4(), ...base }),
    );
  };

  const onSubmit = values => {
    const saleValues = {
      date: values.date,
      start_datetime: values.start_datetime
        ? convertDateTimeToUTCDateTime(values.start_datetime)
        : null,
      end_datetime: values.end_datetime
        ? convertDateTimeToUTCDateTime(values.end_datetime)
        : null,
      saleyard_id: values.saleyard_id, // may be undefined in case of Clearing Sale
      pricing_type_id: values.pricing_type_id,
      species_id: values.species_id,
      sale_type: values.sale_type,
      default_buyer_id: values.default_buyer_id,
      default_property_id: values.default_property_id,
      default_vendor_id: values.default_vendor_id,
      default_vendor_property_id: values.default_vendor_property_id,
      sale_sub_type_id: values.sale_sub_type_id,
      manual_vendor_numbers: !!values.manual_vendor_numbers,
      rounds: values.rounds.map(r => r.value),
      notes: values.notes,
      sale_title: values.sale_title,
      default_buyers_premium: values.default_buyers_premium,
      default_listing_fee_cents: values.default_listing_fee_cents,
      card_transaction_fee_percentage: values.card_transaction_fee_percentage,
      export_sites: getIn(values, "deployment_sales[0].export_sites"),
      using_registered_bidders: values.using_registered_bidders,
      deployment_count: values.deployment_count,
      deployment_sales: values.deployment_sales,
      deployment_ids: values.deploymentIds,
      location_property_id: values.location_property_id,
      // Only consumed on create; the edit path ignores these.
      manager_ids: values.manager_ids || [],
      assignee_ids: values.assignee_ids || [],
    };

    if (isScaleOperator) {
      // Ensure that these values haven't been changed, prevents it from being changed even if set in "initialValues"
      saleValues.species_id = Species.CATTLE;
      saleValues.sale_type = SaleType.BOBBYCALF;
    } else if (saleValues.sale_type === SaleType.CLEARING) {
      saleValues.species_id = Species.CLEARING_SALE;
      saleValues.pricing_type_id = PricingTypes.GROSS;
    } else if (saleValues.sale_type === SaleType.SUNDRY_SALE) {
      saleValues.species_id = Species.SUNDRY_SALE;
      saleValues.pricing_type_id = PricingTypes.GROSS;
    }

    const saleyard = saleyards.find(
      ({ value }) => value === values.saleyard_id,
    );

    if (validateSale(saleValues, isEditMode)) {
      if (isEditMode) {
        // If we are editing, we can only change the date, rounds, default pricing, title, or sale sub type.
        const payload = {};
        const deploymentSalePayload = {};
        const fields = [
          "rounds",
          "sale_title",
          "notes",
          "date",
          "start_datetime",
          "end_datetime",
          "pricing_type_id",
          "species_id",
          "using_registered_bidders",
          "sale_sub_type_id",
          "location_property_id",
        ];
        fields.forEach(field => {
          if (!isEqual(values[field], sale[field])) {
            // Use the saleValues value in the payload as it has been massaged (eg rounds is a array of ids)
            payload[field] = saleValues[field];
          }
        });

        if (values.sale_type === SaleType.CLEARING) {
          const fields = [
            "default_buyers_premium",
            "default_listing_fee_cents",
            "card_transaction_fee_percentage",
            "export_sites",
            "payment_due_date",
            "auctions_plus_url",
          ];
          fields.forEach(field => {
            if (!isEqual(values[field], sale[field])) {
              deploymentSalePayload[field] = saleValues[field];
            }
          });
        }

        if (values.sale_type === SaleType.PADDOCK) {
          const fields = [
            "default_vendor_id",
            "default_vendor_property_id",
            "default_buyer_id",
            "default_property_id",
          ];
          fields.forEach(field => {
            if (!isEqual(values[field], sale[field])) {
              payload[field] = saleValues[field];
            }
          });
        }

        if (values.sale_type === SaleType.OVER_HOOKS) {
          const fields = ["default_buyer_id", "default_property_id"];
          fields.forEach(field => {
            if (!isEqual(values[field], sale[field])) {
              payload[field] = saleValues[field];
            }
          });
        }

        if (hasCanBlockNonAdminChangingWeightsPermission) {
          payload.block_non_admin_changing_weights =
            values.block_non_admin_changing_weights;
        }

        // Check if new deployments have been added (only for saleyard sale types)
        if (SaleyardSaleTypes.includes(values.sale_type)) {
          const originalDeploymentIds = (sale.deployment_sales || []).map(
            ds => ds.deployment_id,
          );
          const newDeploymentSales = values.deployment_sales.filter(
            ds => !originalDeploymentIds.includes(ds.deployment_id),
          );
          if (newDeploymentSales.length > 0) {
            payload.deployment_ids = values.deploymentIds;
            // Include new deployment sales so their configuration
            // (e.g. export sites) is saved alongside them.
            payload.deployment_sales = newDeploymentSales;
          }
        }

        // Existing deployment sales are updated individually via separate API calls.
        // Newly added deployments are handled via deployment_ids in the sale payload above.
        const deploymentSales = values.deployment_sales.reduce((acc, ds) => {
          if (!ds.deployment_sale_id) {
            return acc;
          }
          const original = sale.deployment_sales.find(
            originalDeploymentSale =>
              originalDeploymentSale.deployment_sale_id ===
              ds.deployment_sale_id,
          );

          if (values.sale_type === SaleType.CLEARING) {
            const clearingDeploymentSalePayload = {
              ...deploymentSalePayload,
              updateExistingSaleLotExportSites:
                ds.updateExistingSaleLotExportSites,
              auctions_plus_url: ds.auctions_plus_url,
              vendor_terms: ds.vendor_terms,
              buyer_terms: ds.buyer_terms,
              display_name: ds.display_name,
            };
            if (
              !commissionsAreEqual(
                ds.vendorCommission,
                original.vendorCommission,
              )
            ) {
              clearingDeploymentSalePayload.vendorCommission =
                ds.vendorCommission;
            }
            acc.push({
              payload: clearingDeploymentSalePayload,
              deploymentSaleId: ds.deployment_sale_id,
            });
          } else if (
            !isEqual(ds, original) ||
            values.is_sale_locked !== original.is_sale_locked
          ) {
            const deploymentSale = {
              ...ds,
              is_sale_locked: values.is_sale_locked,
            };
            deploymentSalePayload.market_report_notes = ds.market_report_notes;
            acc.push({
              payload: deploymentSale,
              deploymentSaleId: ds.deployment_sale_id,
            });
          }
          return acc;
        }, []);
        // Skip the sale update when nothing sale-level changed; a roles-only
        // edit would otherwise refetch the sale and clobber the optimistic role
        // update. Roles persist via persistSaleRoles.
        if (Object.keys(payload).length > 0 || deploymentSales.length > 0) {
          dispatch(
            SaleAction.updateWithDeploymentSales({
              salePayload: payload,
              livestockSaleId: values.livestocksale_id,
              deploymentSales,
            }),
          );
        }
        persistSaleRoles(values);
      } else {
        const payload = saleValues;
        payload.saleyard_name = saleyard?.label;
        dispatch(SaleAction.create(payload));
      }
      closeSelf();
    }
  };

  const title = isEditMode ? "Edit Sale" : <Row>New Sale</Row>;

  const selectableSaleTypes = React.useMemo(() => {
    // Listing all "visible" sale types - with lock icons next to unavailable sale types
    return (
      Object.values(SaleType)
        .map(saleType =>
          saleTypes.includes(saleType)
            ? {
                label: saleType,
                value: saleType,
              }
            : {
                label: <DisabledLabel saleType={saleType} />,
                isDisabled: true,
                value: saleType,
              },
        )
        // some sale types we don't want users to be able to select
        .filter(saleType => !UnselectableSaleTypes.includes(saleType.value))
    );
  }, [saleTypes]);

  const currentDeploymentSalesCount = defaults.deployment_count;

  const saleIsLocked = defaults.is_sale_locked;

  // Applies to the agent's own deployment-sale fields (eg Title) - editable
  // by the owning agent even when the sale is locked or has other deployments.
  const consigningAgentReadOnly = isEditMode && isConsigningAgent;

  // Applies to shared, livestock-sale-level fields.
  const readOnly =
    consigningAgentReadOnly ||
    (saleIsLocked && isLivestockAgent) ||
    (currentDeploymentSalesCount > 1 && isLivestockAgent);

  const subTypesExist = filterSaleSubTypes(initial).length > 0;

  return (
    <Formik
      enableReinitialize
      validationSchema={validationSchema(subTypesExist)}
      initialValues={initial}
      onSubmit={onSubmit}
    >
      {isScaleOperator ? (
        <BobbyCalfSaleForm
          buyers={buyers}
          saleyards={saleyards}
          handleClose={closeSelf}
          handleDelete={isEditMode ? onDelete : undefined}
          title={title}
        />
      ) : (
        <SaleForm
          isEditMode={isEditMode}
          saleyards={saleyards}
          handleClose={closeSelf}
          title={title}
          rounds={speciesRounds}
          saleTypes={selectableSaleTypes}
          setSpecies={setInitial}
          handleReInit={handleReInit}
          handleDelete={isEditMode && !readOnly ? onDelete : undefined}
          readOnly={readOnly}
          consigningAgentReadOnly={consigningAgentReadOnly}
        />
      )}
    </Formik>
  );
}

SaleModal.propTypes = {
  closeSelf: PropTypes.func,
  saleyards: PropTypes.array,
  buyers: PropTypes.array,
  initialValues: PropTypes.object,
  isScaleOperator: PropTypes.bool,
  saleId: PropTypes.number,
  validateSale: PropTypes.func,
};

export default SaleModal;
